/*eslint max-lines-per-function: ["error", 300]*/
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, Tab } from 'react-bootstrap';
import { type SlotInfo, type View, Views } from 'react-big-calendar';

import { useSpdCore } from '../../../common/hooks/useSpdCore';
import {
  BigCalendar,
  EventVisit,
  ResourceVisit,
} from '../../../common/components/Calendar/BigCalendar';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import {
  CreateUpdateVisitRegistrationDto,
  GetAppointmentChannelDto,
  GetVisitRegistrationCalendarSlotDto,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import {
  convert,
  newVisit,
  validate,
} from '../../../common/spdCore/contexts/VisitRegistrationContext';
import { ExternalVisitBookingEditor } from '../../components/ExternalVisitBooking/ExternalVisitBookingEditor';
import { ExternalVisitBookingDetails } from '../../components/ExternalVisitBooking/ExternalVisitBookingDetails';
import { ExternalVisitBookingSearch } from '../../components/ExternalVisitBooking/ExternalVisitBookingSearch';
import HealthcareFacilitySelector from '../../components/Selects/HealthcareFacilitySelector';
import { TabsHistory } from '../../../app/components/TabsHistory';
import FormRow from '../../../app/components/FormRow';

type CalendarEventVisit = EventVisit & { id: string };

const slotToVisit = (
  slot: GetVisitRegistrationCalendarSlotDto,
): CreateUpdateVisitRegistrationDto => ({
  firstname: slot.firstname || undefined,
  surname: slot.surname || undefined,
  phone: slot.phone || undefined,
  mail: slot.mail || undefined,
  examTypeId: slot.examTypeId || undefined,
  appointmentChannelId: slot.appointmentChannelId || '',
  visitDateTime: slot.visitDateTime,
  visitTime: slot.visitTime,
  description: slot.description || undefined,
});

export const ExternalVisitBookingCalendarView: React.FC = () => {
  const spd = useSpdCore();

  const [refresh, setRefresh] = useState(false);
  const [modal, setModal] = useState(false);
  const [editorTitle, setEditorTitle] = useState('Nowy zapis');
  const [editingId, setEditingId] = useState<string>();
  const [calendarSlots, setCalendarSlots] = useState<
    GetVisitRegistrationCalendarSlotDto[]
  >([]);
  const [events, setEvents] = useState<CalendarEventVisit[]>([]);
  const [resources, setResources] = useState<ResourceVisit[]>([]);
  const [appointmentChannels, setAppointmentChannels] = useState<
    GetAppointmentChannelDto[]
  >([]);
  const [visitRegistration, setVisitRegistration] =
    useState<CreateUpdateVisitRegistrationDto>();
  const [detailsSlot, setDetailsSlot] =
    useState<GetVisitRegistrationCalendarSlotDto>();
  const [dateRange, setDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({ start: null, end: null });
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState<View>(Views.WEEK);
  const [healthcareFacilityId, setHealthcareFacilityId] = useState<
    string | undefined
  >();

  const handleRangeChange = (range: Date[] | { start: Date; end: Date }) => {
    if (Array.isArray(range)) {
      setDateRange({ start: range[0], end: range[range.length - 1] });
    } else {
      setDateRange(range);
    }
  };

  useEffect(() => {
    spd.externalVisitBooking
      .getAppointmentChannels(healthcareFacilityId)
      .then(data => {
        setAppointmentChannels(data);
        setResources(
          data.map(channel => ({
            resourceId: channel.id,
            resourceTitle: channel.name
              ? `${channel.name} (${channel.healthcareFacility?.displayName || ''})`
              : '',
            healthCareFacilityName:
              channel.healthcareFacility?.displayName || '',
          })),
        );
      })
      .catch(errorResponse => {
        console.log(errorResponse);
      });
  }, [spd.externalVisitBooking, refresh, healthcareFacilityId]);

  useEffect(() => {
    spd.externalVisitBooking
      .getCalendar(
        dateRange.start || undefined,
        dateRange.end || undefined,
        healthcareFacilityId,
      )
      .then(data => {
        setCalendarSlots(data);
        setEvents(
          data.map(item => ({
            id: item.id,
            title: `${item.surname || ''} ${item.firstname || ''}`,
            start: item.visitDateTime,
            end: item.visitDateTime
              ? new Date(
                  item.visitDateTime.getTime() +
                    (item.visitTime || 0) * 60 * 1000,
                )
              : null,
            resourceId: item.appointmentChannelId || '',
            finished: false,
            external: false,
          })),
        );
      })
      .catch(errorResponse => {
        console.log(errorResponse);
      });
  }, [spd.externalVisitBooking, refresh, dateRange, healthcareFacilityId]);

  const openEditor = (
    title: string,
    id: string | undefined,
    visit: CreateUpdateVisitRegistrationDto,
  ) => {
    setEditorTitle(title);
    setEditingId(id);
    setVisitRegistration(visit);
    setModal(true);
  };

  const handleAddEvent = async (slotInfo?: SlotInfo) => {
    openEditor(
      'Nowy zapis',
      undefined,
      newVisit(slotInfo?.start, slotInfo?.end),
    );
  };

  const handleCloseModal = () => {
    setModal(false);
  };

  const onSubmit = async (visit: CreateUpdateVisitRegistrationDto) => {
    if (
      visit.visitDateTime &&
      new Date(visit.visitDateTime).getTime() < Date.now()
    ) {
      return {
        saved: false,
        errors: ['Nie można ustawić terminu wizyty w przeszłości.'],
      };
    }
    const status = await validate(visit);
    if (!status.valid) {
      return { saved: false, errors: status.errors };
    }
    try {
      if (editingId) {
        await spd.externalVisitBooking.updateBooking(editingId, visit);
      } else {
        await spd.externalVisitBooking.createBooking(visit);
      }
      setModal(false);
      setRefresh(prev => !prev);
      return { saved: true, errors: [] };
    } catch (response) {
      return {
        saved: false,
        errors: await responseErrors(response as ResponseError),
      };
    }
  };

  const handleEventClick = (clickInfo: any) => {
    const slot = calendarSlots.find(s => s.id === clickInfo.id);
    if (!slot) {
      return;
    }
    if (slot.mine) {
      openEditor('Edytuj zapis', slot.id, slotToVisit(slot));
    } else {
      setDetailsSlot(slot);
    }
  };

  const getSearchData = useCallback(
    (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: 'ASC' | 'DESC',
      filterText?: string,
      filterColumns?: string,
    ) =>
      spd.externalVisitBooking.getAllByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ),
    [spd.externalVisitBooking],
  );

  const getSearchCount = useCallback(
    (filterText?: string, filterColumns?: string) =>
      spd.externalVisitBooking.getAllCount(filterText, filterColumns),
    [spd.externalVisitBooking],
  );

  const handleSearchRowClick = async (id: string) => {
    const data = await spd.externalVisitBooking.get(id);
    openEditor('Edytuj zapis', id, convert(data));
  };

  const handleReschedule = (info: any) => {
    const slot = calendarSlots.find(s => s.id === info.event.id);
    if (!slot?.mine) {
      return;
    }
    openEditor('Zmień termin wizyty', slot.id, {
      ...slotToVisit(slot),
      visitDateTime: info.start,
      visitTime:
        info.start && info.end
          ? (info.end.getTime() - info.start.getTime()) / (1000 * 60)
          : slot.visitTime,
    });
  };

  return (
    <Card>
      <Card.Header>Wizyty - partnerzy</Card.Header>
      <Card.Body>
        <TabsHistory
          activeKey="calendar"
          id="external-visit-booking-tabs"
          className="mb-3"
        >
          <Tab eventKey="calendar" title="Kalendarz" className="m-3">
            <FormRow controlId="healthcareFacility" label="Placówka">
              <HealthcareFacilitySelector
                value={healthcareFacilityId || ''}
                onChange={e => {
                  setHealthcareFacilityId(
                    e.value === 'All' ? undefined : e.value,
                  );
                }}
                placeholder="Wybierz placówkę"
                className="selector-healthcare mb-3"
                addAll={true}
              />
            </FormRow>

            <BigCalendar
              events={events}
              resources={resources}
              handleEventClick={handleEventClick}
              handleEventDrop={handleReschedule}
              handleEventResize={handleReschedule}
              handleAddEvent={handleAddEvent}
              handleRangeChange={handleRangeChange}
              selectable="ignoreEvents"
              disableAllDaySlotSelection
              date={calendarDate}
              view={calendarView}
              onNavigate={setCalendarDate}
              onView={setCalendarView}
              draggableAccessor={event =>
                calendarSlots.find(
                  s => s.id === (event as CalendarEventVisit).id,
                )?.mine ?? false
              }
            />
          </Tab>
          <Tab eventKey="search" title="Wyszukiwanie" className="m-3">
            <ExternalVisitBookingSearch
              getData={getSearchData}
              getCount={getSearchCount}
              onRowClick={handleSearchRowClick}
              refresh={refresh}
              withFilterInColumns={true}
            />
          </Tab>
        </TabsHistory>
      </Card.Body>
      <ExternalVisitBookingEditor
        id={editingId}
        title={editorTitle}
        show={modal}
        visitRegistration={visitRegistration}
        appointmentChannels={appointmentChannels}
        onCancel={handleCloseModal}
        onSubmit={onSubmit}
      />
      <ExternalVisitBookingDetails
        slot={detailsSlot}
        onHide={() => setDetailsSlot(undefined)}
      />
    </Card>
  );
};
